import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import ReCAPTCHA from "react-google-recaptcha";
import {
  Form,
  Carousel,
  notification,
  Input,
  Switch,
  Button,
  Select,
} from "antd";
import Card from "./Card";
import config from "../../Configurations.js";
import firebase from "../../FirebaseConfig.js";
import { ACCOUNT_PENDING_APPROVAL, rememberAccountReview } from "../Login/accountReview";
import { firebaseIdToken } from "../../util/firebaseIdToken";
import "../../stylesheets/SignForms.scss";
import {
  authWithGmail,
  apiRequest,
  logError,
  signout,
} from "../../util/services";
import styles from "./signup.module.scss";
import SignupLeftPanel from "./SignupLeftPanel";
import SignupCardStep from "./SignupCardStep";
import SurveyCardStep from "./SurveyCardStep";
import AuthLanguageSwitch from "../AuthLanguageSwitch";
import {
  filterByTypeInterval,
  getPlanById,
  getPlanByUTM,
  getPlans,
} from "./plans";
import {
  isFrontDoorSignupProduct,
  isPaidSignupProduct,
  getSignupProductByKey,
  buildSignupSalesCtaUrl,
  persistSignupProductIntent,
  signupProductsForIntent,
  buildSignupPath,
  resolveOnboardingProduct,
} from "./signupProducts";
import SignupFitWizard from "./SignupFitWizard";
import { getPreAuthFailureNotice } from "../Login/preAuthFailureNotice";
import { HIDE_BOARD_TOUR, HIDE_CREATION_TOUR } from "../../util/tourGuide";
import { USER } from "../../util/constants";
import { getParams } from "../../util/common";
import {
  normalizeAppLanguage,
  persistAppLanguage,
  resolvePreferredAppLanguage,
} from "../../util/locale";
import { sendAppVerificationEmail } from "../../util/sendVerificationEmail";
import { useHistory } from "react-router-dom";
import {
  initAnonymousAnalytics,
  trackAnonymousAuthSubmit,
  trackAnonymousFormFieldFill,
} from "../../util/anonymousAnalytics";
import { mergeAuthFieldSources, readInputValue } from "../../util/authFieldAnalytics";
import {
  fetchAuthChallengeStatus,
  verifyAuthChallengeToken,
  trackChallengeShown,
  shouldShowAuthCaptcha,
  isAuthCaptchaSatisfied,
  isAuthCaptchaExemptOrigin,
  buildPreAuthCheckPayload,
} from "../../util/authChallenge";
import {
  InfoCircleOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
const { Option } = Select;

const Signup = ({ user, authenticated }) => {
  const { t, i18n } = useTranslation();
  const [selectedPlan, setSelectedPlan] = useState();
  const [plansCategory, setPlansCategory] = useState("default");
  const [timeType, setTimeType] = useState("monthly");
  const [currency, setCurrency] = useState("usd");
  const [reviews, setReviews] = useState("100000");
  const [expert, setExpert] = useState("expert_no");
  const [plans, setPlans] = useState(getPlans());

  const [checkRecaptcha, setCheckRecaptcha] = useState(!config.RECAPTCHA_ENABLED);
  const [challengeRequired, setChallengeRequired] = useState(false);
  const [challengeStatus, setChallengeStatus] = useState(null);
  const [botBlocked, setBotBlocked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [freeTrial, setFreeTrial] = useState(true);
  const [captchaError, setCaptchaError] = useState("");
  const [resendTimer, setResendTimer] = useState(60);
  const [productLoading, setProductLoading] = useState(null);
  const [pendingSalesProduct, setPendingSalesProduct] = useState(null);
  const [frontDoorStatus, setFrontDoorStatus] = useState(null);
  const [frontDoorProductLabel, setFrontDoorProductLabel] = useState("");
  const frontDoorActivationStarted = useRef(false);
  const urlProductActivationStarted = useRef(false);
  const history = useHistory();
  const [form] = Form.useForm();
  const [demoBanner, setDemoBanner] = useState("");
  const [cardState, setCardState] = useState(null);
  const [surveyNeedsCard, setSurveyNeedsCard] = useState(false);
  const signupFieldRefs = useRef({});
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };
  let carousel = React.createRef();
  let startingStep = 0;
  /* Determine the starting component */
  const UTMparams = getParams(document.location.href);
  const utmPlan = getPlanByUTM(UTMparams.recur, UTMparams.type, UTMparams.plan);
  const urlSignupProduct = resolveOnboardingProduct({
    urlParams: UTMparams,
    intendedProduct: user?.intendedProduct,
  });
  const visibleSignupProducts = signupProductsForIntent(urlSignupProduct);
  const isFrontDoorDeepLink =
    urlSignupProduct && isFrontDoorSignupProduct(urlSignupProduct);
  const isPaidIntentDeepLink =
    Boolean(urlSignupProduct) && isPaidSignupProduct(urlSignupProduct);
  const frontDoorProductMeta = urlSignupProduct
    ? getSignupProductByKey(urlSignupProduct)
    : null;
  const showFrontDoorActivation = Boolean(
    user?.uid && user?.emailVerified && isFrontDoorDeepLink
  );
  const prospectNeedsCard = [
    "sonic_prospect",
    "sonic_prospect_channels",
    "sonic_prospect_ecommerce",
  ].includes(urlSignupProduct);

  if (!user.uid) {
    startingStep = 0;
  } else if (!user.emailVerified) {
    startingStep = 1;
  } else if (user.plan === undefined) {
    startingStep = pendingSalesProduct || isPaidIntentDeepLink ? 3 : 2;
  } else if (user.role || user.company || user.dataSource || user.usage) {
    startingStep = utmPlan ? 4 : 3;
  }

  const invitationId = getParams(document.location.href)?.invitationId;
  const handleNextStepAfterEmailVerification = async () => {
    const payload = { email: user.email };
    const result = await apiRequest("users/add_invited", "POST", payload, true);
    if (result && result?.success) {
      // history.push("/console/myDashboards");
      history.push("/home");
    } else {
      carousel.next();
      window.scrollTo(0, 0);
    }
  };

  const email = getParams(document.location.href)?.email || "";

  const preferredSignupLanguage = () =>
    resolvePreferredAppLanguage({
      search: window.location.search,
      stored:
        typeof window !== "undefined"
          ? window.localStorage.getItem("userLang")
          : null,
      i18nLanguage: i18n.language,
    });

  useEffect(() => {
    const preferred = persistAppLanguage(preferredSignupLanguage());
    if (normalizeAppLanguage(i18n.language) !== preferred) {
      void i18n.changeLanguage(preferred);
    }
    try {
      firebase.auth().languageCode = preferred;
    } catch {
      // ignore
    }
  }, [i18n]);

  useEffect(() => {
    if (!user?.uid || !carousel?.goTo) return;
    if (!user.emailVerified) {
      carousel.goTo(1);
      return;
    }
    if (user.plan === undefined) {
      carousel.goTo(pendingSalesProduct || isPaidIntentDeepLink ? 3 : 2);
    }
  }, [
    user?.uid,
    user?.emailVerified,
    user?.plan,
    pendingSalesProduct,
    isPaidIntentDeepLink,
  ]);

  useEffect(() => {
    setSelectedPlan(utmPlan?.id);
  }, []);

  useEffect(() => {
    if (!urlSignupProduct) return;
    persistSignupProductIntent(urlSignupProduct);
    if (user?.uid && user.intendedProduct !== urlSignupProduct) {
      firebase
        .database()
        .ref(`/users/${user.uid}/intendedProduct`)
        .set(urlSignupProduct);
    }
    if (
      typeof window !== "undefined" &&
      window.location.pathname === "/signup"
    ) {
      const params = new URLSearchParams(window.location.search);
      const lang = preferredSignupLanguage();
      if (!params.get("product")) {
        history.replace(buildSignupPath(urlSignupProduct, lang));
      } else if (params.get("lang") !== lang) {
        params.set("lang", lang);
        history.replace(`${window.location.pathname}?${params.toString()}`);
      }
    }
  }, [urlSignupProduct, user?.uid, user?.intendedProduct, history]);

  useEffect(() => {
    if (!user?.uid) {
      initAnonymousAnalytics();
      if (!config.RECAPTCHA_ENABLED || isAuthCaptchaExemptOrigin()) {
        setCheckRecaptcha(true);
        return;
      }
      // Checkbox is always required when reCAPTCHA is enabled.
      setCheckRecaptcha(false);
      if (!config.RISK_BASED_CAPTCHA) {
        return;
      }
      fetchAuthChallengeStatus().then((status) => {
        if (status?.blocked) {
          setBotBlocked(true);
          setChallengeStatus(status);
          setCheckRecaptcha(false);
          notification.error({
            message: t("signup_page.access_denied"),
            description: t("signup_page.access_denied_desc"),
          });
          return;
        }
        setChallengeStatus(status);
        const required = Boolean(status?.challenge_required);
        setChallengeRequired(required);
        trackChallengeShown(status);
      });
    }
  }, [user?.uid]);

  const handlePayment = (e, freePlan) => {
    e.preventDefault();
    if (freePlan || freeTrial) {
      addPlan(null);
      return;
    }
    // Paddle self-serve checkout decommissioned — paid plans are sales-assisted.
    notification.info({
      message: t("signup_page.paid_unavailable"),
      description: t("signup_page.paid_unavailable_desc"),
      icon: <InfoCircleOutlined />,
    });
  };

  const verifyEmail = async (firstName, uid) => {
    let user = firebase.auth().currentUser;
    const lang = persistAppLanguage(preferredSignupLanguage());
    const result = await sendAppVerificationEmail({
      uid: uid || user?.uid,
      language: lang,
      continueUrl: document.location.href,
      firstName: firstName || user?.displayName,
    });
    if (result && !result.error && !result.cancel) {
      notification.info({
        message: t("signup_page.email_sent"),
        description: t("signup_page.email_sent_desc"),
        icon: <InfoCircleOutlined />,
      });
      setResendTimer(60);
      return;
    }
    notification.error({
      message: t("signup_page.verification_not_sent"),
    });
    setResendTimer(60);
    apiRequest(
      "users/client_log",
      "POST",
      {
        error:
          (user?.email || "") +
          " verification email failed with error: " +
          (result?.error || "send_failed"),
      },
      true
    );
  };

  const showAuthCaptcha = shouldShowAuthCaptcha({ challengeRequired });

  const trackSignupFieldBlur = (fieldName) => (event) => {
    const value = event?.target?.value;
    trackAnonymousFormFieldFill(fieldName, "signup", Boolean(value), value);
  };

  const getSignupAuthFields = (values = {}) =>
    mergeAuthFieldSources(values, form.getFieldsValue(true), {
      firstName: readInputValue(signupFieldRefs.current.firstName),
      lastName: readInputValue(signupFieldRefs.current.lastName),
      email: readInputValue(signupFieldRefs.current.email),
      password: readInputValue(signupFieldRefs.current.password),
      confirm: readInputValue(signupFieldRefs.current.confirm),
    });

  const signUpUser = async (values) => {
    if (user?.uid) {
      setLoading(false);
      if (!user.emailVerified) {
        if (carousel?.goTo) carousel.goTo(1);
        return;
      }
      notification.info({
        message: t("signup_page.already_signed_in"),
        description: t("signup_page.already_signed_in_desc"),
      });
      if (carousel?.goTo) carousel.goTo(2);
      return;
    }

    setCaptchaError("");
    window.localStorage.removeItem(HIDE_BOARD_TOUR);
    window.localStorage.removeItem(HIDE_CREATION_TOUR);

    if (
      showAuthCaptcha &&
      !isAuthCaptchaSatisfied({
        challengeRequired: showAuthCaptcha,
        checkRecaptcha,
      })
    ) {
      setCaptchaError(t("signup_page.recaptcha_required"));
      return;
    }

    const authCheckResult = await apiRequest(
      "users/pre_auth_check",
      "POST",
      buildPreAuthCheckPayload({
        email: values.email,
        operation_type: "signup",
        invitation_id: invitationId,
      }),
      true
    );
    if (
      !authCheckResult ||
      authCheckResult?.cancel === true ||
      authCheckResult?.error
    ) {
      setLoading(false);
      trackAnonymousAuthSubmit("signup", {
        success: false,
        method: "password",
        fields: getSignupAuthFields(values),
      });
      const blocked = authCheckResult?.code === "BOT_BLOCKED" || authCheckResult?.blocked;
      if (blocked) {
        setBotBlocked(true);
      }
      const notice = getPreAuthFailureNotice(authCheckResult, t);
      if (notice.kind === "unavailable") {
        notification[notice.type]({
          message: notice.message,
          description: notice.description,
        });
      } else {
        notification.error({
          message: blocked ? t("signup_page.access_denied") : t("auth.error"),
          description: blocked
            ? t("signup_page.access_denied_desc")
            : authCheckResult?.error || t("signup_page.unauthorized"),
        });
      }
      signout(false);
      return;
    }

    setLoading(true);
    const signupLang = persistAppLanguage(preferredSignupLanguage());
    try {
      firebase.auth().languageCode = signupLang;
    } catch {
      // ignore
    }
    const newUser = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      type: USER,
      emailVerified: false,
      language: signupLang,
    };

    firebase
      .auth()
      .createUserWithEmailAndPassword(values.email, values.password)
      .then(async (response) => {
        const res = response.user.toJSON();
        trackAnonymousAuthSubmit("signup", {
          success: true,
          method: "password",
          fields: getSignupAuthFields(values),
        });
        registerUID(res.uid, response.user, newUser, true);
      })
      .catch(function(error) {
        trackAnonymousAuthSubmit("signup", {
          success: false,
          method: "password",
          fields: getSignupAuthFields(values),
        });
        // var errorCode = error.code;
        apiRequest(
          "users/client_log",
          "POST",
          {
            error:
              values.email + " account creation failed with error: " + error,
          },
          true
        );

        var errorMessage = error.message;
        notification.error({
          message: t("auth.error"),
          description: errorMessage,
        });
        setLoading(false);
      });
  };

  const registerUID = async (uid, authUser, objUser, sendEmail) => {
    const idToken = await firebaseIdToken(authUser);
    var myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    var requestOptions = {
      method: "POST",
      headers: myHeaders,
        body: JSON.stringify({
        user_id: uid,
        email: objUser.email,
        id_token: idToken,
        invitation_id: invitationId,
        first_name: objUser.firstName,
        last_name: objUser.lastName,
        language: persistAppLanguage(preferredSignupLanguage()),
        product: urlSignupProduct || "",
        utm_source: UTMparams.utm_source || "",
        utm_medium: UTMparams.utm_medium || "",
        utm_campaign: UTMparams.utm_campaign || "",
        demo: UTMparams.demo || "",
      }),
    };

    // Once users/create stored the account, its Firebase account must survive whatever fails next:
    // App.js may already have signed the user out (account waiting for approval), and deleting it
    // left a Pivony account nobody could sign in to.
    let created = false;
    await fetch(config.BASE_URL + "users/create", requestOptions)
      .then((response) => response.json())
      .then(async (result) => {
        if (!result || result?.error || result?.cancel === true) {
          setLoading(false);
          authUser.delete();
          notification.error({
            message: t("auth.error"),
            // The API answers in the signup language (throwaway address, signup limit, invitation...).
            description: result?.error || t("signup_page.signup_failed"),
          });
        } else {
          created = true;
          const profile = { ...objUser };
          profile.language = persistAppLanguage(preferredSignupLanguage());
          if (urlSignupProduct) {
            profile.intendedProduct = urlSignupProduct;
            persistSignupProductIntent(urlSignupProduct);
          }
          try {
            await firebase.database().ref(`/users/${uid}`).set(profile);
          } catch (_) {
            // Signed out meanwhile; the API already stored the account.
          }
          if (sendEmail) {
            await verifyEmail(objUser.firstName, uid);
          }
          if (result.pending_approval) {
            rememberAccountReview(ACCOUNT_PENDING_APPROVAL);
            document.location.href = "/login";
            return;
          }
          document.location.href = document.location.href;
        }
        setLoading(false);
      })
      .catch((error) => {
        if (!created) {
          authUser.delete();
        }
        notification.error({
          message: t("auth.error"),
          description: t("signup_page.contact_support"),
        });
        setLoading(false);
      });
  };

  const verifyRecaptcha = async (token) => {
    if (!token) {
      setCheckRecaptcha(false);
      return;
    }
    setCaptchaError("");
    // Google v2 checkbox already verified a human. Accept that for form submit.
    setCheckRecaptcha(true);

    if (!(config.RISK_BASED_CAPTCHA && config.RECAPTCHA_ENABLED)) {
      return;
    }
    if (!challengeRequired) {
      return;
    }

    await verifyAuthChallengeToken(token, {
      reasons: challengeStatus?.reasons || [],
      bot_score: challengeStatus?.bot_score,
    });
  };

  // Gmail sign up removed
  // const signUpWithGmail = async () => {
  //   const result = await authWithGmail();
  //   if (result) {
  //     const authCheckResult = await apiRequest("users/pre_auth_check", "POST", { firebase_email: result.email }, true);
  //     if ((authCheckResult || {}).error) {
  //       notification.error({
  //         message: "Error",
  //         description: authCheckResult.error,
  //       });
  //       signout(false);
  //       return;
  //     }

  //     firebase
  //       .database()
  //       .ref(`/users/${result.uid}`)
  //       .once("value", async (snapshot) => {
  //         if (!snapshot.val()) {
  //           const objUser = {
  //             firstName: result.displayName,
  //             phone: result.phoneNumber,
  //             email: result.email,
  //             type: USER,
  //             emailVerified: true,
  //           };
  //           registerUID(result.uid, result, objUser);
  //         }
  //       });
  //   }
  // };

  const passPlanSection = (planId, freeTrial) => {
    carousel.next();
    window.scrollTo(0, 0);
    setSelectedPlan(planId);
    setFreeTrial(freeTrial);
  };

  const handleSelectProduct = async (productKey) => {
    setProductLoading(productKey);
    if (showFrontDoorActivation && !frontDoorStatus) {
      setFrontDoorStatus("loading");
    }
    const result = await apiRequest(
      "subscriptions/selectProduct",
      "POST",
      {
        user_id: user.uid,
        product_key: productKey,
      },
      true
    );
    setProductLoading(null);
    // Survey Free asks for a card (never charged) before it activates.
    if (result?.error_code === "CARD_REQUIRED" && productKey === "survey") {
      setSurveyNeedsCard(true);
      return;
    }
    if (!result || result.error) {
      if (showFrontDoorActivation) {
        setFrontDoorStatus("error");
      }
      notification.error({
        message: t("auth.error"),
        description: result?.error || t("signup_page.contact_support"),
      });
      return;
    }
    if (result.pending_approval) {
      setFrontDoorProductLabel(
        result.product_label ||
          t(getSignupProductByKey(productKey)?.labelKey || "")
      );
      setFrontDoorStatus("pending");
      return;
    }
    if (result.self_serve) {
      await firebase
        .database()
        .ref(`/users/${user.uid}/plan`)
        .set(result.firebase_plan || productKey);
      await firebase
        .database()
        .ref(`/users/${user.uid}/selectedProduct`)
        .set(productKey);
      history.push(result.redirect_path || "/console/sonic-survey");
      return;
    }
    const salesMeta = getSignupProductByKey(productKey);
    const salesLabel =
      (salesMeta ? t(salesMeta.labelKey) : "") ||
      result.product_label ||
      productKey;
    setPendingSalesProduct(salesLabel);
    if (carousel?.goTo) {
      carousel.goTo(3);
    } else if (carousel?.next) {
      carousel.next();
    }
    window.scrollTo(0, 0);
  };

  const renderProductChooser = () => {
    if (!urlSignupProduct) {
      return (
        <SignupFitWizard
          onSelect={handleSelectProduct}
          loadingKey={productLoading}
        />
      );
    }
    return (
      <>
        <div className={styles.title}>
          <h1>
            {visibleSignupProducts.length === 1
              ? t(visibleSignupProducts[0].labelKey)
              : t("signup_products.title")}
          </h1>
          <p className={styles.welcome}>
            {visibleSignupProducts.length === 1
              ? t(visibleSignupProducts[0].descKey)
              : t("signup_products.subtitle")}
          </p>
        </div>
        <div className={styles.productGrid}>
          {visibleSignupProducts.map((product) => (
            <div
              key={product.key}
              className={`${styles.productCard} ${
                product.selfServe ? styles.productCardSelfServe : ""
              }`}
            >
              {product.badgeKey && (
                <span className={styles.productBadge}>
                  {t(product.badgeKey)}
                </span>
              )}
              <h3>{t(product.labelKey)}</h3>
              <p>{t(product.descKey)}</p>
              <button
                className={styles.btn}
                type="button"
                disabled={Boolean(productLoading)}
                onClick={() => handleSelectProduct(product.key)}
              >
                {productLoading === product.key
                  ? t("signup_products.starting")
                  : product.selfServe
                    ? t("signup_products.select")
                    : t("signup_products.request_approval")}
              </button>
            </div>
          ))}
        </div>
      </>
    );
  };

  useEffect(() => {
    const demo = new URLSearchParams(window.location.search).get("demo");
    if (!demo) return undefined;
    let stop = false;
    apiRequest(
      "subscriptions/sonic-prospect/self-serve/preview",
      "POST",
      { demo },
      true
    ).then((result) => {
      if (!stop && result && result.banner && result.name) {
        setDemoBanner(result.name);
      }
    });
    return () => {
      stop = true;
    };
  }, []);

  useEffect(() => {
    if (!showFrontDoorActivation || !prospectNeedsCard) return undefined;
    let stop = false;
    apiRequest(
      "subscriptions/sonic-prospect/self-serve/status",
      "POST",
      {},
      true
    ).then((result) => {
      if (!stop) {
        setCardState(result || { card_saved: false, requires_card: true });
      }
    });
    return () => {
      stop = true;
    };
  }, [showFrontDoorActivation, prospectNeedsCard, user?.uid]);

  useEffect(() => {
    if (
      !showFrontDoorActivation ||
      !urlSignupProduct ||
      productLoading ||
      frontDoorActivationStarted.current ||
      frontDoorStatus === "pending"
    ) {
      return;
    }
    if (prospectNeedsCard && !cardState?.card_saved) return;
    frontDoorActivationStarted.current = true;
    handleSelectProduct(urlSignupProduct);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFrontDoorActivation, urlSignupProduct, user?.uid, user?.emailVerified, cardState, prospectNeedsCard]);

  useEffect(() => {
    if (
      !user?.uid ||
      !user.emailVerified ||
      user.plan !== undefined ||
      !urlSignupProduct ||
      productLoading ||
      showFrontDoorActivation ||
      urlProductActivationStarted.current
    ) {
      return;
    }
    urlProductActivationStarted.current = true;
    // Paid deep-link / UTM: land on sales CTA immediately, then notify sales.
    if (isPaidIntentDeepLink) {
      const meta = getSignupProductByKey(urlSignupProduct);
      setPendingSalesProduct(
        meta ? t(meta.labelKey) : urlSignupProduct
      );
    }
    handleSelectProduct(urlSignupProduct);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, user?.emailVerified, user?.plan, urlSignupProduct, showFrontDoorActivation]);

  const addPlan = async (subscriptionId) => {
    // carousel.next();
    const payload = {
      user_id: user.uid,
      package_id: getPlanById(selectedPlan).pricing[timeType].id,
      subscription_id: subscriptionId,
      IsTrial: freeTrial,
    };
    const result = await apiRequest(
      "subscriptions/create",
      "POST",
      payload,
      true
    );
    if (result && !result.error) {
      await firebase
        .database()
        .ref(`/users/${user.uid}/plan`)
        .set(getPlanById(selectedPlan).pricing[timeType].id);
      history.push("/");
    } else {
      logError("subscriptions/create endpoint", result);
      notification.error({
        message: result.error || JSON.stringify(result),
      });
    }
  };

  const changePlansCategory = (e) => {
    setPlansCategory(e.target.value);
    setPlans(filterByTypeInterval(timeType, e.target.value));
  };

  const changeTime = (checked) => {
    if (checked) {
      setTimeType("yearly");
      // setPlans(filterByTypeInterval("yearly", plansCategory));
    } else {
      setTimeType("monthly");
      // setPlans(filterByTypeInterval("monthly", plansCategory));
    }
  };
  console.log(authenticated);

  const frontDoorProductName =
    frontDoorProductLabel ||
    (frontDoorProductMeta ? t(frontDoorProductMeta.labelKey) : urlSignupProduct);

  const renderAccountBar = () => (
    <div className={styles.accountBar}>
      <AuthLanguageSwitch />
      <div className={styles.accountBarActions}>
        <span className={styles.accountBarEmail}>
          Signed in as {user.email}
        </span>
        <button
          type="button"
          className={styles.accountBarLogout}
          onClick={signout}
        >
          Log out
        </button>
      </div>
    </div>
  );

  if (surveyNeedsCard) {
    return (
      <SurveyCardStep
        onReady={() => {
          setSurveyNeedsCard(false);
          handleSelectProduct("survey");
        }}
      />
    );
  }

  if (showFrontDoorActivation && prospectNeedsCard && !cardState) {
    return (
      <div className={`${styles.container} ${styles.containerActivation}`}>
        <div className={styles.signUpContainer}>
          {renderAccountBar()}
          <div className={styles.activationPanel}>
            <p className={styles.activationSpinner}>Preparing Sonic Prospect</p>
          </div>
        </div>
      </div>
    );
  }

  if (showFrontDoorActivation && prospectNeedsCard && cardState && !cardState.card_saved) {
    return (
      <SignupCardStep
        brandName={demoBanner || cardState.brand_name}
        termsVersion={cardState.terms_version}
        termsUrl={cardState.terms_url}
        onReady={() => setCardState({ ...cardState, card_saved: true })}
      />
    );
  }

  if (showFrontDoorActivation) {
    return (
      <div className={`${styles.container} ${styles.containerActivation}`}>
        <div className={styles.signUpContainer}>
          {renderAccountBar()}
          <div className={styles.activationPanel}>
            {frontDoorStatus === "pending" ? (
              <>
                <CheckCircleOutlined className={styles.activationIconSuccess} />
                <h1>{t("signup_products.activation.pending_title")}</h1>
                <p>
                  {t("signup_products.activation.pending_desc", {
                    product: frontDoorProductName,
                  })}
                </p>
                <p className={styles.activationNote}>
                  {t("signup_products.activation.pending_note")}
                </p>
              </>
            ) : frontDoorStatus === "error" ? (
              <>
                <CloseCircleOutlined className={styles.activationIcon} />
                <h1>{t("signup_products.activation.error_title")}</h1>
                <p>{t("signup_products.activation.error_desc")}</p>
              </>
            ) : (
              <>
                <InfoCircleOutlined className={styles.activationIcon} />
                <h1>
                  {t("signup_products.activation.title", {
                    product: frontDoorProductName,
                  })}
                </h1>
                <p>{t("signup_products.activation.subtitle")}</p>
                <p className={styles.activationSpinner}>
                  {t("signup_products.starting")}
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (user?.uid && user?.emailVerified && frontDoorStatus === "pending") {
    return (
      <div className={`${styles.container} ${styles.containerActivation}`}>
        <div className={styles.signUpContainer}>
          {renderAccountBar()}
          <div className={styles.activationPanel}>
            <CheckCircleOutlined className={styles.activationIconSuccess} />
            <h1>{t("signup_products.activation.pending_title")}</h1>
            <p>
              {t("signup_products.activation.pending_desc", {
                product: frontDoorProductName,
              })}
            </p>
            <p className={styles.activationNote}>
              {t("signup_products.activation.pending_note")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (user?.uid && !user.emailVerified) {
    return (
      <div className={`${styles.container} ${styles.containerLoggedIn}`}>
        <div className={styles.signUpContainer}>
          {renderAccountBar()}
          <div className={styles.carouselContent}>
            <div className={styles.title}>
              <h1>Verify your email</h1>
              <div className={styles.verified}>
                <img src="/assets/verify_email.svg" alt="verified" />
              </div>
              <div className={styles.welcome}>
                <p>We've sent an email to {user.email}</p>
                <p>
                  Just click the link in the email to confirm your address
                </p>
                <p>If you don't see it, you may need to check your spam</p>
                <div className={styles.resendEmailBlock}>
                  <button onClick={verifyEmail}>Resend email</button>
                </div>
                <p>Your information is safe and secure in Pivony.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (
    user?.uid &&
    user.emailVerified &&
    user.plan === undefined &&
    (pendingSalesProduct || isPaidIntentDeepLink) &&
    !isFrontDoorDeepLink
  ) {
    return (
      <div className={`${styles.container} ${styles.containerLoggedIn}`}>
        <div className={styles.signUpContainer}>
          {renderAccountBar()}
          <div className={styles.carouselContent}>
            <div className={styles.title}>
              <h1>{t("signup_products.sales.title")}</h1>
              <div className={styles.welcome}>
                <p>
                  {t("signup_products.sales.desc", {
                    product:
                      pendingSalesProduct ||
                      t("signup_products.sales.product_fallback"),
                  })}
                </p>
              </div>
            </div>
            <div className={styles.salesActions}>
              <a
                className={styles.btn}
                href={buildSignupSalesCtaUrl(
                  "https://pivony.com/contact",
                  urlSignupProduct || "sales_trial"
                )}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("signup_products.sales.contact")}
              </a>
              <a
                className={styles.btnSecondary}
                href={buildSignupSalesCtaUrl(
                  "https://pivony.com/book-demo",
                  urlSignupProduct || "sales_trial"
                )}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("signup_products.sales.book_demo")}
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (user?.uid && user.emailVerified && user.plan === undefined) {
    return (
      <div className={`${styles.container} ${styles.containerLoggedIn}`}>
        <div className={styles.signUpContainer}>
          {renderAccountBar()}
          <div className={styles.carouselContent}>
            {renderProductChooser()}
          </div>
        </div>
      </div>
    );
  }

  // const MyInterval = setInterval(() => {
  //   setResendTimer(prev => prev - 1)
  // }, 1000)

  // useEffect(() => {
  //   if (resendTimer === 0) return;
  //   else {
  //     setTimeout(() => {
  //       setResendTimer(prev => prev - 1)
  //     }, 1000);
  //   }
  // }, [resendTimer]);

  const showSignupHero =
    (authenticated === "unlogged" || authenticated === "unknown") && !user?.uid;

  return (
    <div
      className={`${styles.container} ${
        showSignupHero
          ? undefined
          : user?.uid
          ? styles.containerLoggedIn
          : undefined
      }`}
    >
      {showSignupHero ? (
        <SignupLeftPanel intentProductKey={urlSignupProduct} />
      ) : null}
      <div className={styles.signUpContainer}>
        {user?.uid ? renderAccountBar() : (
          <div className={styles.signupLangSwitch}>
            <AuthLanguageSwitch />
          </div>
        )}
        <Carousel
          swipe={false}
          accessibility={false}
          ref={(node) => (carousel = node)}
          dotPosition="top"
          initialSlide={startingStep}
        >
          <div className={styles.carouselContent}>
            <div className={styles.title}>
              <img src="/assets/TopicScope_logo_only.png" alt="" />
              {demoBanner ? (
                <p className={styles.demoBanner}>
                  Continuing with your {demoBanner} assistant
                </p>
              ) : null}
              <h4 className={styles.subTitle1}>{t("signup_page.get_started")}</h4>
              <p className={styles.subTitle2}>{t("signup_page.create_account")}</p>
              <p className={styles.loginLink}>
                {t("signup_page.already_have_account")}{" "}
                <span
                  onClick={() =>
                    history.push(
                      `/login?lang=${normalizeAppLanguage(i18n.language)}`
                    )
                  }
                >
                  {t("signup_page.log_in")}
                </span>
              </p>
            </div>

            <div className={styles.form}>
              <Form
                form={form}
                name="mainDetails"
                onFinish={signUpUser}
              >
                <Form.Item
                  name="firstName"
                  label={t("signup_page.first_name")}
                  rules={[
                    { required: true, message: t("signup_page.first_name_required") },
                  ]}
                >
                  <input
                    type="text"
                    className={styles.inputForm}
                    ref={(node) => {
                      signupFieldRefs.current.firstName = node;
                    }}
                    onBlur={trackSignupFieldBlur("firstName")}
                  />
                </Form.Item>
                <Form.Item
                  name="lastName"
                  label={t("signup_page.last_name")}
                  rules={[{ required: true, message: t("signup_page.last_name_required") }]}
                >
                  <input
                    type="text"
                    className={styles.inputForm}
                    ref={(node) => {
                      signupFieldRefs.current.lastName = node;
                    }}
                    onBlur={trackSignupFieldBlur("lastName")}
                  />
                </Form.Item>
                <Form.Item
                  name="email"
                  label={t("signup_page.email")}
                  rules={[
                    {
                      type: "email",
                      message: t("signup_page.valid_email"),
                    },
                    { required: true, message: t("signup_page.email_required") },
                  ]}
                >
                  <input
                    type="text"
                    className={styles.inputForm}
                    ref={(node) => {
                      signupFieldRefs.current.email = node;
                    }}
                    onBlur={trackSignupFieldBlur("email")}
                  />
                </Form.Item>
                <Form.Item
                  name="password"
                  label={t("signup_page.password")}
                  className={styles.passwordBox}
                  rules={[
                    { required: true, message: t("signup_page.password_required") },
                    { min: 6, message: t("signup_page.weak_password") },
                  ]}
                >
                  <Input.Password
                    className={styles.inputForm}
                    ref={(node) => {
                      signupFieldRefs.current.password = node;
                    }}
                    onBlur={trackSignupFieldBlur("password")}
                  />
                </Form.Item>
                <Form.Item
                  name="confirm"
                  label={t("signup_page.confirm_password")}
                  className={styles.passwordBox}
                  dependencies={["password"]}
                  hasFeedback
                  rules={[
                    {
                      required: true,
                      message: t("signup_page.confirm_password_required"),
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("password") === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error(t("signup_page.passwords_mismatch"))
                        );
                      },
                    }),
                  ]}
                >
                  {/* <input type="password" className={styles.inputForm} /> */}
                  <Input.Password
                    className={styles.inputForm}
                    ref={(node) => {
                      signupFieldRefs.current.confirm = node;
                    }}
                    onBlur={trackSignupFieldBlur("confirm")}
                  />
                </Form.Item>

                <Form.Item style={{ textAlign: "center" }}>
                  {showAuthCaptcha && (
                    <div className={styles.recaptcha}>
                      <ReCAPTCHA
                        style={{ display: "inline-block" }}
                        onChange={verifyRecaptcha}
                        onExpired={() => verifyRecaptcha(null)}
                        sitekey={config.RECAPTCHA_SITE_PUBLIC_KEY}
                      />
                    </div>
                  )}
                  {captchaError && (
                    <p
                      style={{
                        color: "#ff4d4f",
                        margin: "0",
                        textAlign: "center",
                      }}
                    >
                      {captchaError}
                    </p>
                  )}
                  <Button
                    loading={loading}
                    className={styles.btn}
                    type="primary"
                    htmlType="submit"
                    style={{ height: "auto" }}
                  >
                    {t("signup_page.sign_up")}
                  </Button>
                </Form.Item>
              </Form>
            </div>
            {/* <h2 className={styles.or}>
            <span>or</span>
          </h2>
          <div>
            <button onClick={signUpWithGmail} className={styles.gmailSignup}>
              <img src="/assets/google.png" />
              <span>SIGN UP WITH GOOGLE</span>
            </button>
          </div> */}

            <p className={styles.terms}>
              {t("signup_page.terms_agree")} <br />
              <a href="https://pivony.com/terms/" target="_blank" rel="noreferrer">
                {t("signup_page.terms_of_service")}
              </a>{" "}
              {t("signup_page.and")}{" "}
              <a href="https://pivony.com/privacy/" target="_blank" rel="noreferrer">
                {t("signup_page.privacy_policy")}
              </a>
            </p>
          </div>
          <div className={styles.carouselContent}>
            <div className={styles.title}>
              <h1>Verify your email</h1>
              <div className={styles.verified}>
                <img
                  src={
                    !user.emailVerified
                      ? "/assets/verify_email.svg"
                      : "/assets/verify_email_done.svg"
                  }
                  alt="verified"
                />
              </div>
              {!user.emailVerified && (
                <div className={styles.welcome}>
                  <p>We've sent an email to {user.email}</p>
                  <p>
                    Just click the link in the email to confirm your address
                  </p>
                  <p>If you don't see it, you may need to check your spam</p>
                  <div className={styles.resendEmailBlock}>
                    {/* {resendTimer > 0 && <p>Didn't receive the email? Try after {resendTimer} seconds</p>} */}
                    <button onClick={verifyEmail}>Resend email</button>
                  </div>
                  <p>Your information is safe and secure in Pivony.</p>
                </div>
              )}
              {user.emailVerified && (
                <div
                  className={`${styles.welcome} ${styles.emailVerifiedBlock}`}
                >
                  <p>Your email {user.email} is successfully verified!</p>
                  <button
                    className={styles.btn}
                    type="primary"
                    onClick={handleNextStepAfterEmailVerification}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
            <div className={styles.form}></div>
          </div>
          <div className={styles.carouselContent}>
            {renderProductChooser()}
          </div>
          <div className={styles.carouselContent}>
            <div className={styles.title}>
              <h1>{t("signup_products.sales.title")}</h1>
              <div className={styles.welcome}>
                <p>
                  {t("signup_products.sales.desc", {
                    product: pendingSalesProduct || t("signup_products.sales.product_fallback"),
                  })}
                </p>
              </div>
            </div>
            <div className={styles.salesActions}>
              <a
                className={styles.btn}
                href={buildSignupSalesCtaUrl(
                  "https://pivony.com/contact",
                  urlSignupProduct || "sales_trial"
                )}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("signup_products.sales.contact")}
              </a>
              <a
                className={styles.btnSecondary}
                href={buildSignupSalesCtaUrl(
                  "https://pivony.com/book-demo",
                  urlSignupProduct || "sales_trial"
                )}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("signup_products.sales.book_demo")}
              </a>
            </div>
            <div className={styles.blogPost}>
              <button
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                className={styles.logoutBtn}
                onClick={signout}
              >
                <img
                  src={
                    isHovered
                      ? "/assets/log-out.svg"
                      : "/assets/log-out-black.svg"
                  }
                  alt=""
                />
                <span>Logout</span>
              </button>
            </div>
            <div className={styles.onboardingBackground}>
              <img src="/assets/onboarding.svg" alt="" />
            </div>
            <div className={styles.form}></div>
          </div>
          {user.plan && (
            <div className={styles.carouselContent_fullWidth}>
              <div className={styles.title}>
                <h1>The right plan for your company's growth</h1>
              </div>
              <div className={styles.planFilters}>
                <div className={styles.switch}>
                  <p>Monthly</p>
                  <Switch onChange={changeTime} />
                  <p>Annually</p>
                </div>
              </div>

              <div className={styles.DashboardsWrapper}>
                <div className={styles.Dashboards}>
                  <p>Currency</p>
                  <Select
                    labelInValue
                    defaultValue={{ value: "usd" }}
                    style={{ width: 100 }}
                    onChange={({ value }) => setCurrency(value)}
                  >
                    <Option value="usd">USD $</Option>
                    <Option value="eur">EUR €</Option>
                    <Option value="gbp">GBP £</Option>
                  </Select>
                </div>
              </div>

              {user.currency && (
                <div className={styles.plans}>
                  {plans.map((plan) => (
                    <Card
                      plan={plan}
                      passPlanSection={passPlanSection}
                      reviews={reviews}
                      expert={expert}
                      timeType={timeType}
                      currency={currency}
                      user={user}
                      freeTrial={false}
                    />
                  ))}
                </div>
              )}
              <p>*Prices do not include VAT</p>
            </div>
          )}
          {user.plan && (
            <div className={styles.carouselContent_fullWidth}>
              <div className={styles.title}>
                <h1>The right plan for your company's growth</h1>
              </div>
              <div className={styles.plans_selected}>
                {user.currency && (
                  <Card
                    plan={{ ...getPlanById(selectedPlan), hidden: true }}
                    passPlanSection={passPlanSection}
                    reviews={reviews}
                    expert={expert}
                    timeType={timeType}
                    currency={currency}
                    user={user}
                    freeTrial={freeTrial}
                  />
                )}
                <div className={styles.selectionDetails}>
                  <h3>
                    You have selected the {getPlanById(selectedPlan)?.title}{" "}
                    plan{" "}
                    {!freeTrial &&
                      !getPlanById(selectedPlan)?.free &&
                      `${timeType} subscription`}
                    {freeTrial && `free trial`}
                  </h3>
                  <h5>
                    Not sure of this plan? pick another{" "}
                    <a
                      onClick={() => {
                        carousel.prev();
                        window.scrollTo(0, 0);
                      }}
                    >
                      here
                    </a>
                  </h5>
                  <div className={styles.payment}>
                    {!freeTrial && !getPlanById(selectedPlan)?.free ? (
                      <>
                        <h3>Paid plan</h3>
                        <p>
                          Self-serve card checkout is no longer available.
                          Contact support@pivony.com to activate this plan.
                        </p>
                      </>
                    ) : (
                      <>
                        <h3>{!freeTrial ? "Free Plan" : "Free Trial"}</h3>
                        <p>
                          To start a {!freeTrial ? "free plan" : "free trial"},
                          you don't need to enter your credit card details.
                        </p>
                        <p>
                          Click to start your {!freeTrial ? "free" : "trial"}{" "}
                          membership.
                        </p>
                      </>
                    )}
                    <button
                      className={styles.btn}
                      style={{
                        float: "inherit",
                        margin: "20px 0 0 0",
                        width: "100%",
                      }}
                      type="primary"
                      onClick={(e) =>
                        handlePayment(
                          e,
                          getPlanById(selectedPlan).title === "Free"
                        )
                      }
                    >
                      {freeTrial
                        ? "Activate Free Trial"
                        : getPlanById(selectedPlan)?.free
                        ? "Begin My Subscription"
                        : "Contact Sales"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          {user.plan && (
            <div className={styles.carouselContent}>
              <div className={styles.title}>
                <h1>You are all set</h1>
                <div className={styles.verified}>
                  <img src="/assets/verified.png" alt="verified" />
                </div>
                <div className={styles.welcome}>
                  <p>Thank you for joining us!</p>
                  <p>
                    You can now start your subscription, we will guide you
                    through
                  </p>
                  <button
                    className={styles.btn}
                    style={{ float: "inherit", margin: "20px 0 0 0" }}
                    type="primary"
                    onClick={addPlan}
                  >
                    Start
                  </button>
                </div>
              </div>
              <div className={styles.form}></div>
            </div>
          )}
        </Carousel>
      </div>
    </div>
  );
};

export default Signup;
